"use client";

import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { History, Lock, RefreshCw, RotateCcw, Trash2 } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useDeleteSnippetVersion, useSnippetVersion, useSnippetVersions } from "../hooks";
import { collapseUnchanged, diffSnippetSources } from "../lib/snippet-diff";

const triggerClass =
  "inline-flex h-11 min-h-[44px] min-w-[44px] shrink-0 items-center justify-center gap-2 rounded-lg border border-white/15 bg-white/5 px-0 text-sm font-medium text-white/90 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C] md:h-10 md:min-h-0 xl:min-w-0 xl:px-3";

type SnippetHistoryPanelProps = {
  snippetId: string;
  /** The buffer as it stands, so a version is diffed against what is on screen. */
  currentSource: string;
  /** `pro` and `internal` may restore; a downgraded owner still reads (ADR-0049). */
  canRestore: boolean;
  /** Confirms over a dirty buffer, then applies and saves. Returns false if cancelled. */
  onRestore: (source: string) => void;
  onUpgradeClick: () => void;
};

function formatWhen(value: string | undefined, locale: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

/**
 * The owner's version history for the snippet in the editor — `PG-IDE05`.
 *
 * Rendered only for a saved snippet the caller owns; there is no cross-user
 * path into this data and the list is fetched from an owner-only route.
 */
export function SnippetHistoryPanel({
  snippetId,
  currentSource,
  canRestore,
  onRestore,
  onUpgradeClick,
}: SnippetHistoryPanelProps) {
  const t = useTranslations("playground");
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: versions, isLoading, isError, refetch, isFetching } = useSnippetVersions(
    snippetId,
    open,
  );
  const { data: selected, isLoading: selectedLoading } = useSnippetVersion(snippetId, selectedId);
  const deleteVersion = useDeleteSnippetVersion(snippetId);

  const diff = useMemo(
    () => (selected ? diffSnippetSources(selected.sourceCode, currentSource) : null),
    [selected, currentSource],
  );
  const rows = useMemo(() => (diff ? collapseUnchanged(diff.lines) : []), [diff]);

  const list = versions ?? [];

  return (
    <DropdownMenu.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSelectedId(null);
      }}
    >
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          title={t("historyTitle")}
          aria-label={t("historyTitle")}
          className={triggerClass}
        >
          <History className="h-4 w-4 shrink-0 text-[#E94D1D]" aria-hidden />
          <span className="hidden xl:inline">{t("historyTitle")}</span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[200] w-[min(100vw-2rem,460px)] rounded-xl border border-white/10 bg-[#141416] p-1.5 shadow-2xl"
          sideOffset={6}
          align="end"
          collisionPadding={8}
        >
          <div className="flex items-start justify-between gap-3 px-1.5 pb-2 pt-1">
            <div>
              <p className="text-sm font-semibold text-white">{t("historyTitle")}</p>
              <p className="text-xs text-white/50">{t("historySubtitle")}</p>
            </div>
            {isFetching && !isLoading ? (
              <span className="mt-1 h-2 w-2 animate-pulse rounded-full bg-[#E94D1D]" aria-hidden />
            ) : null}
          </div>

          {isLoading ? (
            <div className="space-y-1 px-1 py-1" aria-label={t("loading")}>
              {[0, 1, 2].map((item) => (
                <div key={item} className="min-h-12 animate-pulse rounded-lg bg-white/5" />
              ))}
            </div>
          ) : isError ? (
            <div className="px-3 py-5 text-center">
              <p className="text-sm text-white/60">{t("historyLoadFailed")}</p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-lg border border-white/15 px-3 text-sm font-medium text-white transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45"
              >
                <RefreshCw className="h-4 w-4 text-[#E94D1D]" aria-hidden />
                {t("historyRetry")}
              </button>
            </div>
          ) : list.length === 0 ? (
            <div className="px-3 py-5 text-center">
              <p className="text-sm font-medium text-white">{t("historyEmpty")}</p>
              <p className="mt-1 text-xs leading-relaxed text-white/50">
                {canRestore ? t("historyEmptyPro") : t("historyEmptyFree")}
              </p>
              {canRestore ? null : (
                <button
                  type="button"
                  onClick={onUpgradeClick}
                  className="mx-auto mt-3 flex min-h-11 items-center gap-2 rounded-lg bg-[#E94D1D] px-3 text-sm font-semibold text-white transition hover:bg-[#d44315] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFAB8F]"
                >
                  {t("historyUpgradeCta")}
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="max-h-[min(30vh,220px)] overflow-y-auto py-0.5">
                {list.map((version) => {
                  const when = formatWhen(version.createdAt, locale);
                  const isSelected = version._id === selectedId;
                  return (
                    <button
                      key={version._id}
                      type="button"
                      onClick={() => setSelectedId(isSelected ? null : version._id)}
                      aria-pressed={isSelected}
                      className={cn(
                        "flex min-h-12 w-full items-center gap-2.5 rounded-lg border border-transparent px-2.5 py-2 text-left text-sm text-white/90 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45",
                        isSelected && "border-[#E94D1D]/35 bg-[#E94D1D]/10",
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{when ?? version.title}</span>
                        <span className="mt-0.5 block truncate text-xs text-white/45">
                          {t("historyRowMeta", {
                            title: version.title,
                            chars: version.sourceChars,
                          })}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {selectedId ? (
                <div className="mt-1.5 border-t border-white/10 px-1.5 pt-2">
                  {selectedLoading || !diff ? (
                    <p className="py-4 text-center text-xs text-white/50">{t("loading")}</p>
                  ) : (
                    <>
                      <p className="mb-1.5 text-xs text-white/55">
                        {diff.toobig
                          ? t("historyDiffTooBig")
                          : t("historyDiffSummary", { added: diff.added, removed: diff.removed })}
                      </p>
                      {diff.toobig ? null : (
                        <div className="max-h-[min(28vh,200px)] overflow-auto rounded-lg border border-white/10 bg-[#0B0B0C] p-2 font-mono text-[11px] leading-relaxed">
                          {rows.map((row, index) =>
                            row.kind === "gap" ? (
                              <p
                                key={`gap-${index}`}
                                className="select-none py-1 text-center text-white/30"
                              >
                                {t("historyDiffHidden", { n: row.hidden })}
                              </p>
                            ) : (
                              <p
                                key={`${row.kind}-${index}`}
                                className={cn(
                                  "whitespace-pre-wrap break-all",
                                  row.kind === "added" && "bg-emerald-500/10 text-emerald-200",
                                  row.kind === "removed" && "bg-red-500/10 text-red-200",
                                  row.kind === "same" && "text-white/45",
                                )}
                              >
                                <span className="select-none pr-2 text-white/25" aria-hidden>
                                  {row.kind === "added" ? "+" : row.kind === "removed" ? "-" : " "}
                                </span>
                                {row.text || " "}
                              </p>
                            ),
                          )}
                        </div>
                      )}

                      <div className="mt-2 flex flex-wrap items-center justify-end gap-2 pb-1">
                        <button
                          type="button"
                          onClick={() => {
                            deleteVersion.mutate(selectedId, {
                              onSuccess: () => {
                                setSelectedId(null);
                                toast.success(t("historyDeleted"));
                              },
                              onError: () => toast.error(t("historyDeleteFailed")),
                            });
                          }}
                          disabled={deleteVersion.isPending}
                          className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-red-400/25 bg-red-500/10 px-3 text-sm font-medium text-red-200 transition hover:bg-red-500/20 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400/40"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                          {t("historyDelete")}
                        </button>
                        {canRestore ? (
                          <button
                            type="button"
                            onClick={() => {
                              if (!selected) return;
                              onRestore(selected.sourceCode);
                              setOpen(false);
                            }}
                            className="inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-[#E94D1D] px-3 text-sm font-semibold text-white transition hover:bg-[#d44315] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFAB8F]"
                          >
                            <RotateCcw className="h-4 w-4" aria-hidden />
                            {t("historyRestore")}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={onUpgradeClick}
                            className="inline-flex min-h-11 items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-3 text-sm font-medium text-white/80 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45"
                          >
                            <Lock className="h-4 w-4 text-white/50" aria-hidden />
                            {t("historyRestoreLocked")}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
